/**
 * Given a list of keys, return a function that takes an object
 * and returns a new object with only those specified keys remaining
 *
 * @group Utilities
 */
export const pick = <T, K extends [...(keyof T)[]]>(keys: K) => {
  return (obj: T) => Object.fromEntries(keys.map((key) => [key, obj[key]])) as Pick<T, K[0]>
}

/**
 * Converts object to array of objects, representing the key-value pairs
 * from the original object. Each new object has a new property, representing
 * the key it was indexed by in the original object.
 *
 * Useful for easier iteration on nested objects
 *
 * The typings are a little crazy looking, but they ensure keys don't get overwritten
 * and type info is not lost.
 *
 * @group Utilities
 *
 * @example
 * const obj = { a: { b: 1 }, c: { d: 2 } }
 * toKeyedArray(obj, 'key') // [{ b: 1, key: 'a' }, { d: 2, key: 'c' }]
 */
export const toKeyedArray = <OldObj extends AnyRecord, Key extends string>(
  obj: OldObj,
  keyName: NotKeyOf<OldObj[keyof OldObj], Key>
): (OldObj[keyof OldObj] & {
  [key in Key]: keyof OldObj
})[] => {
  return Object.entries(obj).map(([keyValue, value]) => ({
    ...value,
    [keyName]: keyValue
  }))
}

/**
 * asserts that Key is not a key of Obj
 */
type NotKeyOf<Obj, Key extends PropertyKey> = Key extends keyof Obj ? never : Key

/**
 * Any object
 */
type AnyRecord = Record<string, any> // oxlint-disable-line typescript/no-explicit-any

/**
 *  extracts all keys that are guaranteed to be in T (i.e., if union type, only shared keys)
 */
export type Keys<T> = keyof T

/**
 * extracts all available keys from a union type, not just shared keys
 */
export type AllKeys<T> = T extends unknown ? Keys<T> : never

/**
 * turn optional field into required
 */
export type RequireKey<Obj, Key extends keyof Obj> = Obj & {
  [P in Key]-?: Obj[P]
}

/**
 *  filters union types to ones that have the specified key
 */
type SubtypesWithKey<Obj, Key extends PropertyKey> = Obj extends unknown
  ? Key extends keyof Obj
    ? Obj
    : never
  : never

/**
 * check if obj has a key with a non-null value
 *
 * @group Utilities
 */
export function has<Key extends AllKeys<T>, T extends {}>(
  key: Key,
  obj: T | null | undefined
): obj is RequireKey<SubtypesWithKey<T, Key>, Key> {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }
  return key in obj && isNonNil(obj[key])
}

/**
 * curried version of `has`
 *
 * @group Utilities
 */
export const curriedHas =
  <Key extends AllKeys<T>, T extends {}>(key: Key) =>
  (obj: T): obj is RequireKey<SubtypesWithKey<T, Key>, Key> => {
    return key in obj && isNonNil(obj[key])
  }

/**
 * type guard to narrow out null and undefined values -- useful for array filtering
 *
 * @group Utilities
 */
export const isNonNil = <T>(val: T | undefined | null): val is T => {
  return val !== null && val !== undefined
}

/**
 * type guard to check if null or undefined
 *
 * @group Utilities
 */
export const isNil = <T>(val: T | undefined | null): val is undefined | null => {
  return val === null || val === undefined
}

/**
 * groups elements of an array using an equality check provided by the accessor
 */
export const groupBy = <T, Accessor extends (item: T) => string | number>(
  arr: T[],
  accessor: Accessor
) =>
  arr.reduce(
    (counts, next) => {
      const key = accessor(next)
      counts[key] ??= []
      counts[key].push(next)
      return counts
    },
    {} as Record<string, T[]>
  ) as Record<ReturnType<Accessor>, T[]>

type GroupAndReduceConfig<T, U> = {
  getGroupingKey: (item: T) => string | number
  accumulateRest: (accumulator: U, next: T) => U
  /**
   * a function that handles the first element of each group (which is guaranteed to exist)
   * turning it into the initial value for the accumulator to use on the next iteration.
   * If not provided, the first element will be used as the initial value.
   */
  handleFirst?: (item: T) => U
}

/**
 * Runs a groupBy, and then reduces the values in each Record value's array.
 */
export function groupAndReduce<T, U = T>(
  list: T[],
  config: {
    getGroupingKey: (item: T) => string | number
    accumulateRest: (accumulator: U, next: T) => U
    /**
     * a function that handles the first element of each group (which is guaranteed to exist)
     * turning it into the initial value for the accumulator to use on the next iteration.
     * If not provided, the first element will be used as the initial value.
     */
    handleFirst?: (item: T) => U
  }
): U[] // Overload 2: Different accumulator type with initializer

/**
 * Runs a groupBy, and then reduces the values in each Record value's array.
 */
export function groupAndReduce<T>(
  list: T[],
  equalityCheck: (item: T) => string | number,
  reducer: (accumulator: T, next: T) => T
): T[] // Overload 1: Same type for accumulator and elements

export function groupAndReduce<T, U = T>(
  list: T[],
  config: ((item: T) => string | number) | GroupAndReduceConfig<T, U>,
  reducer?: (accumulator: T, next: T) => T
) {
  // A plain object accumulator would work for most keys, but JS always iterates
  // integer-like string keys (e.g. "5120") in ascending numeric order ahead of
  // every other key, regardless of insertion order — silently reordering groups
  // whose key happens to be a numeral. A Map preserves insertion order for any key.
  if (typeof config === 'function') {
    const groups = new Map<string | number, T>()
    for (const item of list) {
      const key = config(item)
      groups.set(
        key,
        groups.has(key) ? (reducer as (acc: T, item: T) => T)(groups.get(key) as T, item) : item
      )
    }
    return Array.from(groups.values())
  }

  const groups = new Map<string | number, U>()
  for (const item of list) {
    const key = config.getGroupingKey(item)
    groups.set(
      key,
      groups.has(key)
        ? config.accumulateRest(groups.get(key) as U, item)
        : config.handleFirst
          ? config.handleFirst(item)
          : (item as unknown as U)
    )
  }
  return Array.from(groups.values())
}

/**
 *  Returns a new object with the same keys as the input object, but with each value transformed by the given function.
 * Do not use unless you are sure the object has no inherited properties.
 */
export const mapValues = <T, U>(obj: Record<string, T>, fn: (value: T) => U) =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(value)])) as Record<
    string,
    U
  >

/**
 * Returns a new object with the same keys as the input object, but with each value transformed by the given function.
 * Do not use unless you are sure the object has no inherited properties.
 *
 * Differs from mapValues in that the key is also passed to the function
 */
export const mapEntries = <T, U>(obj: Record<string, T>, fn: (key: string, value: T) => U) =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, fn(key, value)])) as Record<
    string,
    U
  >

/**
 * Shorthand for Object.values
 */
export const values = Object.values

export const entries = Object.entries as <T extends object>(obj: T) => [keyof T, T[keyof T]][]

/**
 * Returns a new object with the specified key or keys omitted. Does not mutate the original object.
 */
export const omit = <T extends object, K extends keyof T>(
  object: T,
  key: K | readonly K[]
): Omit<T, K> => {
  const keysToOmit: readonly K[] = Array.isArray(key) ? key : [key]
  return Object.fromEntries(
    Object.entries(object).filter(([k]) => !keysToOmit.includes(k as K))
  ) as Omit<T, K>
}

export const shallowEquals = <T extends AnyRecord>(a: T, b: T) => {
  if (a === b) {
    return true
  }

  const aKeys = Object.keys(a)

  if (aKeys.length !== Object.keys(b).length) {
    return false
  }

  return aKeys.every((key) => a[key] === b[key])
}

/**
 *  Returns true if all key-value pairs in b are present in a, and the values are equal
 */
export const isMatch = <T extends AnyRecord>(a: T, b: Partial<T>) => {
  return Object.entries(b).every(([key, value]) => a[key] === value)
}

type Primitive = string | number | boolean | null | undefined

/**
 * Converts an object to a URLSearchParams object,
 * removing any keys with null or undefined values
 * and converting numeric and boolean values to strings.
 */
export const toURLSearchParams = (
  obj: Record<string, Primitive | string[]> = {}
): URLSearchParams => {
  const params = new URLSearchParams()

  Object.entries(obj).forEach(([key, value]) => {
    if (isNonNil(value)) {
      if (Array.isArray(value)) {
        value.forEach((item) => params.append(key, item.toString()))
      } else {
        params.append(key, value.toString())
      }
    }
  })
  return params
}

/**
 * Converts an object to a URLSearchParams object,
 * removing any keys with null or undefined values
 * and converting numeric and boolean values to strings.
 * Returns the string representation of the URLSearchParams object.
 */
export const toURLSearchParamString = (
  obj: Record<string, Primitive | string[]> = {},
  addQueryPrefix = false
): string => {
  const params = toURLSearchParams(obj)
  return addQueryPrefix ? `?${params.toString()}` : params.toString()
}

// recursive helper for recursivelyConvertDatesToNumbers
const _convertDates = (obj: unknown): unknown => {
  if (obj instanceof Date) {
    return obj.getTime()
  }

  if (Array.isArray(obj)) {
    return obj.map(_convertDates)
  }

  if (obj instanceof Set) {
    return new Set([...obj].map(_convertDates))
  }

  if (obj instanceof Map) {
    return new Map([...obj].map(([k, v]) => [_convertDates(k), _convertDates(v)]))
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, _convertDates(v)]))
  }
  return obj
}

/**
 * Converts all Date objects to numbers in a value, recursively.
 * For use when serializing an object for storage, such as in a DynamoDB table.
 */
export const recursivelyConvertDatesToNumbers = <T extends object>(obj: T): DatesToNums<T> => {
  return _convertDates(obj) as DatesToNums<T>
}

// DON'T EMULATE THIS MONSTROSITY, especially outside of the utils package
type DatesToNums<T> = T extends Date
  ? number
  : T extends Array<infer U>
    ? DatesToNums<U>[]
    : T extends Set<infer U>
      ? Set<DatesToNums<U>>
      : T extends Map<infer K, infer V>
        ? Map<DatesToNums<K>, DatesToNums<V>>
        : T extends object
          ? { [K in keyof T]: DatesToNums<T[K]> }
          : T

type DefaultType<T, U extends T | undefined> = U extends undefined ? T | undefined : T

/**
 * Given an array of orderable items, returns the item that is the best fit
 * according to the condition function. The condition function should return
 * true if the first argument is a better fit than the second argument.
 *
 * If the array is empty, returns the `defaultValue` (if none provided, returns `undefined`).
 * If there is a tie, returns the first item that tied.
 *
 * Useful for finding the object with the latest timestamp, the lowest value, etc.
 */
export function bestFitBy<T, U extends T | undefined = undefined>(
  arr: T[],
  condition: (a: T, b: T) => boolean,
  defaultValue?: U
): DefaultType<T, U> {
  if (arr.length === 0) {
    return defaultValue as DefaultType<T, U>
  }

  return arr.reduce((bestSoFar, next) => (condition(next, bestSoFar) ? next : bestSoFar))
}

/**
 * Generate a URL path with the given query parameters, converting them to strings if necessary and
 * stripping out any null or undefined values (see `toURLSearchParams` for more details).
 *
 * This assumes that the path does not already have query parameters.
 *
 * @example
 * path('/tailor/__demo__/reports/activity/fields', { season: 2021, crop: 'corn' }) // /tailor/__demo__/reports/activity/fields?season=2021&crop=corn
 */
export const path = (path: string, queryParams?: Record<string, Primitive | string[]>) => {
  if (!path.endsWith('?')) {
    path += '?'
  }

  return path + toURLSearchParamString(queryParams)
}

/**
 * Converts a given object into a FormData instance.
 * Each key-value pair in the object is appended to the FormData.
 *
 * It supports various types:
 * - Strings and Files/Blobs are appended as-is.
 * - Numbers and booleans are converted to strings.
 * - Dates are converted to ISO strings.
 * - Arrays are recursively appended.
 * - Undefined values are skipped.
 *
 * This is meant to mimic the behavior of JSON serialization, but for FormData,
 * and the supported types are based on the available `coerce` types for Zod schemas,
 * so that round tripping through Zod validation works as expected.
 */
export const toFormData = (obj: AnyRecord): FormData => {
  const form = new FormData()
  Object.entries(obj).forEach(([key, value]) => appendValue(form, key, value))
  return form
}

// helper function separate from `toFormData` to allow for recursive appending in the case of arrays
const appendValue = (form: FormData, key: string, value: unknown) => {
  if (value instanceof Blob || typeof value === 'string') {
    form.append(key, value)
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    form.append(key, value.toString())
  } else if (value instanceof Date) {
    form.append(key, value.toISOString())
  } else if (Array.isArray(value)) {
    value.forEach((item) => appendValue(form, key, item))
  } else if (value === undefined) {
    // skip undefined values
  } else {
    // for now, we have no use case for other types, but if we ever do, we can extend this
    // in the meantime, it's not clear what the "correct" behavior would be, since FormData
    // is really just designed for strings and Blobs, and even numbers are converted to strings
    // but reversing the conversion is straightforward in that case
    throw new TypeError(
      `Invalid value for form-data: ${key} must be a string, number, boolean, date, or Blob (or an array of those types)`
    )
  }
}
